import { useQuery } from "@tanstack/react-query";
import { APIResponse } from "@/core/types/api";
import { createFirebaseAuthAxios } from "@/lib/firebase/authAxios";
import { customerShopService } from "./customer-shop.service";
import type {
  CustomerCart,
  CustomerOrder,
  CustomerProduct,
  CustomerShopSummary,
  MarketplaceResponse,
} from "../types/customer-shop";
import type { MarketplaceQueryFilters } from "../types/marketplace-filters";

const api = createFirebaseAuthAxios();
const shopApi = customerShopService(api);

export function useCustomerMarketplace(filters?: MarketplaceQueryFilters) {
  return useQuery<APIResponse<MarketplaceResponse>, Error>({
    queryKey: [
      "customer-marketplace",
      filters?.search ?? "",
      filters?.locationSearch ?? "",
      filters?.county ?? "",
      filters?.subCounty ?? "",
      filters?.ward ?? "",
      filters?.location ?? "",
      filters?.subLocation ?? "",
      filters?.village ?? "",
      filters?.category ?? "",
    ],
    queryFn: () => shopApi.getMarketplace(filters),
  });
}

export function useCustomerShop(shopId?: string, category?: string) {
  return useQuery<
    APIResponse<{
      shop: CustomerShopSummary;
      products: MarketplaceResponse["products"];
    }>,
    Error
  >({
    queryKey: ["customer-shop", shopId, category ?? ""],
    queryFn: () => shopApi.getShop(shopId as string, { category }),
    enabled: !!shopId,
  });
}

export function useProductSearch(
  query: string,
  options?: { enabled?: boolean; limit?: number },
) {
  const trimmed = query.trim();
  return useQuery<APIResponse<CustomerProduct[]>, Error>({
    queryKey: ["product-search", trimmed, options?.limit ?? 8],
    queryFn: () => shopApi.searchProducts(trimmed, options?.limit),
    enabled: (options?.enabled ?? true) && trimmed.length >= 2,
  });
}

export function useCustomerProduct(productId?: string) {
  return useQuery<APIResponse<CustomerProduct>, Error>({
    queryKey: ["customer-product", productId],
    queryFn: () => shopApi.getProduct(productId as string),
    enabled: !!productId,
  });
}

export function useCustomerCart(options?: { enabled?: boolean }) {
  return useQuery<APIResponse<CustomerCart | null>, Error>({
    queryKey: ["customer-cart"],
    queryFn: () => shopApi.getCart(),
    enabled: options?.enabled ?? true,
  });
}

export function useCustomerOrders() {
  return useQuery<APIResponse<CustomerOrder[]>, Error>({
    queryKey: ["customer-orders"],
    queryFn: () => shopApi.getOrders(),
  });
}

export function useCustomerOrder(orderId?: string) {
  return useQuery<APIResponse<CustomerOrder>, Error>({
    queryKey: ["customer-order", orderId],
    queryFn: () => shopApi.getOrder(orderId as string),
    enabled: !!orderId,
  });
}
